import { Check, Clock, UserCheck, CheckCircle2, AlertTriangle } from "lucide-react";

export const APPEL_SLOTS = [
  { key: "s0810", label: "08h10" },
  { key: "s1300", label: "13h00" },
  { key: "s1400", label: "14h00" },
  { key: "s1645", label: "16h45" },
  { key: "setude", label: "Étude du soir" },
];

export function Stat({ label, value, color, testId }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="inline-block w-2.5 h-2.5" style={{ background: color }} />
      <span className="text-[var(--ink-soft)]">{label}</span>
      <span className="font-mono-spec font-semibold text-[var(--ink)]">{value}</span>
    </div>
  );
}

function SlotBtn({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border transition ${
        active ? "bg-[var(--maroon)] text-white border-[var(--maroon)]" : "bg-white text-[var(--ink-soft)] border-[var(--parchment-2)] hover:border-[var(--ink)]"
      }`}
    >
      {icon} {label}
    </button>
  );
}

function StatusToggle({ active, color, label, icon, disabled, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition ${
        active ? "text-white" : "text-[var(--ink-soft)] bg-white border-[var(--parchment-2)] hover:border-[var(--ink)]"
      } ${disabled && !active ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
      style={active ? { background: color, borderColor: color } : {}}
    >
      {icon} {label}
    </button>
  );
}

function StudentRow({ row, idx, canEdit, busy, onMark }) {
  const { student, status } = row;
  return (
    <div className="grid grid-cols-12 items-center gap-4 py-3" data-testid={`appel-row-${student.id}`}>
      <div className="col-span-1 text-xs font-mono-spec text-[var(--muted)]">#{String(idx + 1).padStart(2, "0")}</div>
      <div className="col-span-6 sm:col-span-5">
        <div className="font-medium text-[var(--ink)]">{student.last_name.toUpperCase()} {student.first_name}</div>
      </div>
      <div className="col-span-5 sm:col-span-6 flex items-center justify-end gap-2 flex-wrap">
        <StatusToggle active={status === "present"} color="var(--ok)" label="Présent" icon={<UserCheck size={14} />} disabled={!canEdit || busy} onClick={() => canEdit && onMark("present")} testId={`mark-present-${student.id}`} />
        <StatusToggle active={status === "late"} color="var(--just)" label="Retard" icon={<Clock size={14} />} disabled={!canEdit || busy} onClick={() => canEdit && onMark("late")} testId={`mark-late-${student.id}`} />
        <StatusToggle active={status === "absent"} color="var(--late)" label="Absent" icon={<Check size={14} />} disabled={!canEdit || busy} onClick={() => canEdit && onMark("absent")} testId={`mark-absent-${student.id}`} />
      </div>
    </div>
  );
}

function ValidationBanner({ call, canEdit, busy, onValidate }) {
  if (call) {
    return (
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-[var(--ok)]/10 border border-[var(--ok)]/30 px-4 py-3 text-sm" data-testid="appel-validated-banner">
        <span className="inline-flex items-center gap-2 text-[var(--ok)] font-medium">
          <CheckCircle2 size={16} /> Appel validé{call.done_by_name ? ` par ${call.done_by_name}` : ""}{call.done_at ? ` à ${new Date(call.done_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}` : ""}
        </span>
        {canEdit && (
          <button onClick={onValidate} disabled={busy === "validate"} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="appel-revalidate-btn">
            Revalider (après correction)
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3 bg-[var(--just)]/10 border border-[var(--just)]/40 px-4 py-3 text-sm" data-testid="appel-not-validated-banner">
      <span className="inline-flex items-center gap-2 text-[var(--just)] font-medium">
        <AlertTriangle size={16} /> Appel non encore validé pour ce créneau
      </span>
      {canEdit && (
        <button onClick={onValidate} disabled={busy === "validate"} className="btn-primary !py-1.5 !px-4 text-xs" data-testid="appel-validate-btn">
          <Check size={13} /> Valider l'appel
        </button>
      )}
    </div>
  );
}

function StudentsList({ loading, data, canEdit, busy, onMark }) {
  if (loading) {
    return <div className="mt-6 text-[var(--ink-soft)]">Chargement...</div>;
  }
  if (!data || data.rows.length === 0) {
    return <div className="mt-6 text-[var(--ink-soft)]">Aucun élève dans cette classe.</div>;
  }
  return (
    <div className="mt-6 divide-y divide-[var(--parchment-2)]" data-testid="appel-students-list">
      {data.rows.map((row, idx) => (
        <StudentRow
          key={row.student.id}
          row={row}
          idx={idx}
          canEdit={canEdit}
          busy={busy === row.student.id}
          onMark={(status) => onMark(row.student.id, status)}
        />
      ))}
    </div>
  );
}

export default function DailyView({ date, setDate, slot, setSlot, data, loading, busy, canEdit, onMark, onValidate }) {
  const absentCount = data?.rows.filter((r) => r.status === "absent").length || 0;
  const lateCount = data?.rows.filter((r) => r.status === "late").length || 0;
  const presentCount = data?.rows.filter((r) => r.status === "present").length || 0;
  const call = data?.call;

  return (
    <div className="bg-white border border-[var(--parchment-2)] p-6">
      <div className="flex flex-wrap items-end justify-between gap-4 pb-4 border-b border-[var(--parchment-2)]">
        <div>
          <div className="eyebrow">Appel de classe</div>
          <h2 className="font-serif-display text-2xl font-semibold mt-1">Tous présents par défaut — cochez les absents et retards</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1" data-testid="slot-toggle">
            {APPEL_SLOTS.map((s) => (
              <SlotBtn key={s.key} active={slot === s.key} onClick={() => setSlot(s.key)} icon={<Clock size={13} />} label={s.label} testId={`slot-${s.key}`} />
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <span className="eyebrow">Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none font-mono-spec text-sm"
              data-testid="appel-date-input"
            />
          </label>
        </div>
      </div>

      {!loading && data && <ValidationBanner call={call} canEdit={canEdit} busy={busy} onValidate={onValidate} />}

      <div className="mt-4 flex flex-wrap gap-6 text-sm">
        <Stat label="Présents" value={presentCount} color="var(--ok)" testId="stat-present" />
        <Stat label="Retards" value={lateCount} color="var(--just)" testId="stat-late" />
        <Stat label="Absents" value={absentCount} color="var(--late)" testId="stat-absent" />
      </div>

      <StudentsList loading={loading} data={data} canEdit={canEdit} busy={busy} onMark={onMark} />
    </div>
  );
}
